import {
  dssChainData,
  dssDebtToken,
  dssConfigFor,
  skyLendersByChain,
  usddLendersByChain,
} from '@1delta/data-sdk'
import { UsddVatAbi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { dssLenderKey } from '../../../lending/public-data/dss/convertPublic'
import { dssIlkBytes32 } from '../../../lending/public-data/dss/fetchPublic'

/**
 * `dss` (MakerDAO-shaped CDP) oracle price fetcher — Sky and its forks
 * (USDD 2.0).
 *
 * **Why this exists even though the collateral is already priced elsewhere.**
 * Sky's collateral is WETH / wstETH / WBTC, which our shared price map covers
 * from many sources. What it does NOT cover is the price the PROTOCOL uses,
 * and that is the only price that decides a liquidation: Maker liquidates on
 * `ink × spot < art × rate`, where `spot` comes from the OSM. Feeding that
 * value into the per-market ORACLE channel (`meta.oraclePrice`, read by
 * `getOraclePrice` for risk math) makes our health factor agree with what the
 * protocol will actually enforce, while `meta.price` keeps the live market
 * quote for display. The two channels are deliberately separate — this never
 * overwrites the spot price other lenders use.
 *
 * **Reading it without the OSM whitelist.** `OSM.peek()` is callable only by
 * whitelisted readers, so we never touch the pip. `Vat.ilks(ilk).spot` is the
 * OSM price already divided by the liquidation ratio and by `par`; multiplying
 * it back by `Spot.ilks(ilk).mat` recovers the feed price:
 *
 *   price = (spot / RAY) × (mat / RAY)
 *
 * Verified 2026-08 against market: ETH-A $1,921.91 vs $1,930.75 spot,
 * WSTETH-A $2,384.53 vs $2,395.84, WBTC-A $64,995 vs $65,325 — all ~0.5 %
 * low, uniformly, which is Maker's deliberate **1-hour OSM delay** rather
 * than an error. That lag is a feature here: it is exactly the staleness the
 * liquidation engine itself acts on.
 *
 * **Why `mat` comes from metadata, not a live read.** `Vat.ilks` and
 * `Spot.ilks` share the name `ilks` AND the same selector while returning
 * different tuples, and this runner takes ONE abi per group — a merged abi
 * would decode the Spot result against the Vat's 5-tuple. So only `spot` is
 * read live and `mat` comes from the ilk row. That is also the exactly-right
 * pairing: the converter sets `collateralFactor = 1 / mat`, so when both use
 * the same `mat` it cancels and our health factor reduces to Maker's own
 * `ink × spot ≥ art × rate`. `mat` only moves on a governance vote (months
 * apart) and the generator re-snapshots it each run; in the window between a
 * `mat` change and the next metadata refresh the derived price is off by the
 * ratio of the two values.
 *
 * The debt token (DAI / USDD) is emitted at par, matching the converter.
 */

interface DssMarketMeta {
  lender: string
  chainId: string
  ilk: string
  collToken: string
  debtToken?: string
  /** Liquidation ratio (ray) from the ilk row — see the header note. */
  mat: string
}

/** Every dss lender configured on a chain (Sky + forks). */
const dssLendersByChain = (chainId: string): string[] => [
  ...skyLendersByChain(chainId),
  ...usddLendersByChain(chainId),
]

/** One read per ilk: `Vat.ilks` for `spot` (`mat` rides on the meta). */
export function getDssCalls(chainId: string): FetcherResult<DssMarketMeta>[] {
  const results: FetcherResult<DssMarketMeta>[] = []

  for (const lender of dssLendersByChain(chainId)) {
    const cfg = dssConfigFor(lender, chainId)
    const markets = dssChainData(lender, chainId)?.markets ?? []
    if (!cfg?.vat || markets.length === 0) continue
    const debtToken = dssDebtToken(cfg)

    for (const m of markets) {
      if (!m.ilk || !m.collToken || !m.mat) continue
      const calls: MulticallCall[] = [
        { address: cfg.vat, name: 'ilks', params: [dssIlkBytes32(m.ilk)] },
      ]
      results.push({
        calls,
        meta: {
          lender,
          chainId,
          ilk: m.ilk,
          collToken: m.collToken.toLowerCase(),
          debtToken: debtToken?.toLowerCase(),
          mat: String(m.mat),
        },
        lender: dssLenderKey(lender, chainId, m.ilk),
      })
    }
  }

  return results
}

const RAY = 1e27

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (v == null || v === '0x') return 0n
    return BigInt(v)
  } catch {
    return 0n
  }
}

/** Tuple field access — decoded structs arrive named or positional. */
const field = (res: any, name: string, idx: number): bigint => {
  const v = res?.[name]
  // NB: never trust a named lookup blindly on a positional decode — viem
  // returns arrays here, and an Array.prototype member (`join`, `keys`, …)
  // would shadow the field. Only accept a bigint/number/string.
  if (typeof v === 'bigint' || typeof v === 'number' || typeof v === 'string') {
    return big(v)
  }
  return big(res?.[idx])
}

/**
 * Recover the OSM feed price from `spot × mat` and emit it against the ilk's
 * market, plus the debt token at par.
 */
export function parseDssResults(
  data: any[],
  meta: DssMarketMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const key = dssLenderKey(meta.lender, chainId, meta.ilk)
  const entries: OraclePriceEntry[] = []

  const spot = field(data?.[0], 'spot', 2)
  const mat = big(meta.mat)
  if (spot > 0n && mat > 0n) {
    const collUSD = (Number(spot) / RAY) * (Number(mat) / RAY)
    // Sanity-bound (same posture as the Liquity/River fetchers): a
    // mis-scaled read is dropped rather than allowed to poison risk math,
    // and `getOraclePrice` then falls back to the market price.
    if (collUSD > 0 && collUSD < 1e9) {
      entries.push({
        asset: meta.collToken,
        price: collUSD,
        priceUSD: collUSD,
        marketUid: createMarketUid(chainId, key, meta.collToken),
        targetLender: key,
        description: 'Maker OSM via Vat.spot × Spot.mat (USD, ~1h delayed)',
      })
    }
  }

  if (meta.debtToken) {
    entries.push({
      asset: meta.debtToken,
      price: 1,
      priceUSD: 1,
      marketUid: createMarketUid(chainId, key, meta.debtToken),
      targetLender: key,
      description: 'dss debt token (par)',
      staticBase: true,
      baseAsset: meta.debtToken,
    })
  }

  return entries
}

/** Vat only — see the header note on why `Spot.ilks` is not read here. */
export function getDssAbi() {
  return UsddVatAbi
}

/** dss (Sky / USDD) Oracle Fetcher module. */
export const dssFetcher = {
  getCalls: getDssCalls,
  parse: parseDssResults,
  getAbi: getDssAbi,
}
